/**
 * @fileoverview System Icons for the application
 * @author Pramod Thapa
 * @version 1.0.0
 *
 */

/**
 * SystemIcons.js
 *
 * This file contains a collection of system icons used throughout the application.
 * It exports various icon components that can be imported and used in other parts of the project.
 *
 * The icons are implemented using SVG images and Next.js Image component.
 * They support size variants through the use of class-variance-authority (cva).
 *
 * Usage:
 * import { IconName } from "@/lib/SystemIcons";
 *
 * <IconName size="sm" className="custom-class" />
 *
 * Note: If no size prop is provided, the default size will be applied.
 *
 * Available size variants: "default", "sm", "lg", "xl"
 *
 * List of available icons:
 * - Globe
 * - User
 * - BankTransfer
 * - Upload
 * - FileCheck
 * - Expand
 * - CheckReceipt
 * - CheckSquare
 * - Identification
 * - Suitcase
 * - Lock
 * - Link
 * - DeactiveUser
 * - Home
 * - Pointer
 * - ThreeDot
 * - SpecialRequests
 * - CustomerSupport
 * - PersonalizedService
 * - AddressPoint
 * - Comment
 *
 * Each icon component accepts standard HTML attributes and a 'size' prop.
 *
 * @module SystemIcons
 */

import Image from "next/image";
import { cva } from "class-variance-authority";
import React from "react";

import user from "@/public/icons/user.svg";
import bankTransfer from "@/public/icons/bankTransfer.svg";
import upload from "@/public/icons/upload.svg";
import filecheck from "@/public/icons/filecheck.svg";
import expand from "@/public/icons/expand.svg";
import checkRecipt from "@/public/icons/checkRecipt.svg";
import checkSquare from "@/public/icons/squarecheck.svg";
import identification from "@/public/icons/identification.svg";
import suitcase from "@/public/icons/suitcase.svg";
import lock from "@/public/icons/lock.svg";
import link from "@/public/icons/link.svg";
import deactiveuser from "@/public/icons/deactiveuser.svg";
import home from "@/public/icons/home.svg";
import pointer from "@/public/icons/pointer.svg";
import threedot from "@/public/icons/3dot.svg";
import specialrequests from "@/public/icons/special-requests.svg";
import customersupport from "@/public/icons/customer-support.svg";
import personalizedservice from "@/public/icons/personalized-service.svg";
import addresspoint from "@/public/icons/addresspoint.svg";
import comment from "@/public/icons/comment.svg";

import { cn } from "./utils";

const IconVariants = cva("text-midnightIndigo-400", {
  variants: {
    size: {
      default: "w-6 h-6",
      sm: "w-5 h-5",
      lg: "w-7 h-7",
      xl: "w-9 h-9",
    },
  },
  defaultVariant: {
    size: "default",
  },
});

export const Globe = React.forwardRef(({ className, size, ...props }) => (
  <svg
    width="20"
    height="21"
    viewBox="0 0 20 21"
    fill="none"
    className={cn(IconVariants({ size, className }))}
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.04102 10.0312C6.04102 5.58206 7.67471 1.90625 9.99935 1.90625C12.324 1.90625 13.9577 5.58206 13.9577 10.0312C13.9577 14.4804 12.324 18.1562 9.99935 18.1562C7.67471 18.1562 6.04102 14.4804 6.04102 10.0312ZM12.7077 10.0312C12.7077 6.19617 11.3566 3.15625 9.99935 3.15625C8.64209 3.15625 7.29102 6.19617 7.29102 10.0312C7.29102 13.8663 8.64209 16.9062 9.99935 16.9062C11.3566 16.9062 12.7077 13.8663 12.7077 10.0312Z"
      fill="currentColor"
    />
    <path
      d="M17.0827 6.90625C17.4279 6.90625 17.7077 7.18607 17.7077 7.53125C17.7077 7.84766 17.4726 8.10916 17.1675 8.15054L17.0827 8.15625H2.91602C2.57084 8.15625 2.29102 7.87643 2.29102 7.53125C2.29102 7.21484 2.52614 6.95334 2.83121 6.91196L2.91602 6.90625H17.0827Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.16667 1.90625H10.8333C14.8604 1.90625 18.125 5.17084 18.125 9.19792V10.8646C18.125 14.8917 14.8604 18.1562 10.8333 18.1562H9.16667C5.13959 18.1562 1.875 14.8917 1.875 10.8646V9.19792C1.875 5.17084 5.13959 1.90625 9.16667 1.90625ZM10.8333 3.15625H9.16667C5.82995 3.15625 3.125 5.8612 3.125 9.19792V10.8646C3.125 14.2013 5.82995 16.9062 9.16667 16.9062H10.8333C14.1701 16.9062 16.875 14.2013 16.875 10.8646V9.19792C16.875 5.8612 14.1701 3.15625 10.8333 3.15625Z"
      fill="currentColor"
    />
    <path
      d="M17.0827 11.9062C17.4279 11.9062 17.7077 12.1861 17.7077 12.5312C17.7077 12.8477 17.4726 13.1092 17.1675 13.1505L17.0827 13.1562H2.91602C2.57084 13.1562 2.29102 12.8764 2.29102 12.5312C2.29102 12.2148 2.52614 11.9533 2.83121 11.912L2.91602 11.9062H17.0827Z"
      fill="currentColor"
    />
  </svg>
));

Globe.displayName = "Globe";

export const Cart = React.forwardRef(({ className, size, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="29"
    height="28"
    viewBox="0 0 29 28"
    fill="none"
    className={cn(IconVariants({ size, className }))}
    {...props}
  >
    <path
      d="M4.29167 0.206543C4.80741 0.206543 5.25307 0.566824 5.36114 1.07112L9.73614 21.4878C9.86271 22.0784 9.48649 22.6599 8.89584 22.7864C8.30519 22.913 7.72376 22.5368 7.5972 21.9461L3.40646 2.39404H1.375C0.821277 2.39404 0.363659 1.98257 0.291235 1.44871L0.28125 1.30029C0.28125 0.696232 0.770939 0.206543 1.375 0.206543H4.29167Z"
      fill="currentColor"
    />
    <path
      d="M23.6146 20.623C24.2186 20.623 24.7083 21.1127 24.7083 21.7168C24.7083 22.3209 24.2186 22.8105 23.6146 22.8105H9.03125C8.42719 22.8105 7.9375 22.3209 7.9375 21.7168C7.9375 21.1127 8.42719 20.623 9.03125 20.623H23.6146Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M19.9688 24.2689C19.9688 22.2553 21.601 20.623 23.6146 20.623C25.6281 20.623 27.2604 22.2553 27.2604 24.2689C27.2604 26.2824 25.6281 27.9147 23.6146 27.9147C21.601 27.9147 19.9688 26.2824 19.9688 24.2689ZM25.0729 24.2689C25.0729 23.4635 24.42 22.8105 23.6146 22.8105C22.8092 22.8105 22.1562 23.4635 22.1562 24.2689C22.1562 25.0743 22.8092 25.7272 23.6146 25.7272C24.42 25.7272 25.0729 25.0743 25.0729 24.2689Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.38672 24.2689C5.38672 22.2553 7.01901 20.623 9.03255 20.623C11.0461 20.623 12.6784 22.2553 12.6784 24.2689C12.6784 26.2824 11.0461 27.9147 9.03255 27.9147C7.01901 27.9147 5.38672 26.2824 5.38672 24.2689ZM10.4909 24.2689C10.4909 23.4635 9.83797 22.8105 9.03255 22.8105C8.22714 22.8105 7.57422 23.4635 7.57422 24.2689C7.57422 25.0743 8.22714 25.7272 9.03255 25.7272C9.83797 25.7272 10.4909 25.0743 10.4909 24.2689Z"
      fill="currentColor"
    />
    <path
      d="M26.1671 4.58154C26.96 4.58154 27.7078 4.95002 28.1909 5.57872C28.6739 6.20742 28.8373 7.02492 28.6331 7.79101L26.3006 16.541C26.0027 17.6583 24.9909 18.4357 23.8346 18.4357H7.73049C7.12643 18.4357 6.63674 17.946 6.63674 17.342C6.63674 16.7379 7.12643 16.2482 7.73049 16.2482H23.8346C23.9998 16.2482 24.1443 16.1372 24.1869 15.9775L26.5194 7.22754C26.5486 7.1181 26.5253 7.00131 26.4563 6.9115C26.3873 6.82168 26.2804 6.76904 26.1671 6.76904H5.23047C4.62641 6.76904 4.13672 6.27935 4.13672 5.67529C4.13672 5.07123 4.62641 4.58154 5.23047 4.58154H26.1671Z"
      fill="currentColor"
    />
  </svg>
));

Cart.displayName = "Cart";

export const User = React.forwardRef(({ className, size, ...props }, ref) => (
  <Image src={user} ref={ref} alt="user " className={cn(IconVariants({ size, className }))} {...props} />
));

User.displayName = "User";

export const Package = React.forwardRef(({ className, size, ...props }, ref) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="35"
    height="36"
    viewBox="0 0 35 36"
    fill="none"
    className={cn(IconVariants({ size, className }))}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M4.599 8.69475L14.8077 2.8014C16.0486 2.08573 17.5764 2.08573 18.8173 2.8014L29.0256 8.69453C30.2666 9.41026 31.0312 10.7352 31.0312 12.1678V23.9555C31.0312 25.3891 30.267 26.7129 29.026 27.4286L18.8173 33.322C17.5764 34.0376 16.0486 34.0376 14.8073 33.3217L4.59825 27.4282C3.35748 26.7106 2.59375 25.3872 2.59375 23.9541V12.1678C2.59375 10.7342 3.358 9.41048 4.599 8.69475ZM27.9323 10.5892L17.7244 4.69633C17.1599 4.37075 16.4651 4.37075 15.901 4.69611L5.69227 10.5895C5.12835 10.9147 4.78125 11.5159 4.78125 12.1678V23.9541C4.78125 24.6061 5.12853 25.2079 5.69266 25.5341L15.9006 31.427C16.4651 31.7526 17.1599 31.7526 17.724 31.4273L27.9327 25.5339C28.4966 25.2087 28.8438 24.6075 28.8438 23.9555V12.1678C28.8438 11.5168 28.4961 10.9144 27.9323 10.5892Z"
      fill="currentColor"
    />
    <path
      d="M9.30295 6.14816C9.5798 5.66861 10.1649 5.47802 10.6635 5.68222L10.797 5.74777L23.9074 13.3165C24.1975 13.484 24.3921 13.7732 24.4418 14.0985L24.4543 14.2638V19.3154C24.4543 19.9195 23.9647 20.4092 23.3606 20.4092C22.8069 20.4092 22.3493 19.9977 22.2768 19.4638L22.2668 19.3154L22.2654 14.8938L9.70334 7.64224C9.22379 7.36539 9.0332 6.78024 9.23739 6.28169L9.30295 6.14816Z"
      fill="currentColor"
    />
    <path
      d="M7.11612 22.2378C7.39356 21.7586 7.97894 21.5687 8.47724 21.7735L8.61069 21.8393L13.044 24.4059C13.5668 24.7086 13.7452 25.3777 13.4426 25.9005C13.1651 26.3797 12.5798 26.5696 12.0815 26.3648L11.948 26.2991L7.51467 23.7324C6.9919 23.4297 6.81346 22.7606 7.11612 22.2378Z"
      fill="currentColor"
    />
    <path
      d="M16.8125 16.9673C17.3662 16.9673 17.8238 17.3788 17.8963 17.9126L17.9063 18.061V32.761C17.9063 33.3651 17.4166 33.8548 16.8125 33.8548C16.2588 33.8548 15.8012 33.4433 15.7287 32.9094L15.7188 32.761V18.061C15.7188 17.457 16.2084 16.9673 16.8125 16.9673Z"
      fill="currentColor"
    />
    <path
      d="M28.9998 9.76242C29.5229 9.46041 30.1919 9.63967 30.4939 10.1628C30.7707 10.6424 30.6432 11.2444 30.217 11.574L30.0935 11.6569L17.3593 19.0083C17.0693 19.1758 16.7215 19.1997 16.415 19.0801L16.2656 19.0083L3.53147 11.6569C3.00832 11.3549 2.82906 10.686 3.13107 10.1628C3.40792 9.68327 3.99307 9.49267 4.49162 9.69686L4.62515 9.76242L16.8125 16.7982L28.9998 9.76242Z"
      fill="currentColor"
    />
  </svg>
));

Package.displayName = "Package";

export const BankTransfer = React.forwardRef(({ className, size, ...props }, ref) => (
  <Image
    src={bankTransfer}
    ref={ref}
    alt="bank transfer"
    className={cn(IconVariants({ size, className }))}
    {...props}
  />
));

BankTransfer.displayName = "BankTransfer";

export const Upload = React.forwardRef(({ className, size, ...props }, ref) => (
  <Image src={upload} ref={ref} alt="upload" className={cn(IconVariants({ size, className }))} {...props} />
));

Upload.displayName = "Upload";

export const FileCheck = React.forwardRef(({ className, size, ...props }, ref) => (
  <Image src={filecheck} ref={ref} alt="file check" className={cn(IconVariants({ size, className }))} {...props} />
));

FileCheck.displayName = "FileCheck";

export const Expand = React.forwardRef(({ className, size, ...props }, ref) => (
  <Image src={expand} ref={ref} alt="expand" className={cn(IconVariants({ size, className }))} {...props} />
));

Expand.displayName = "Expand";

export const CheckRecipt = React.forwardRef(({ className, size, ...props }, ref) => (
  <Image src={checkRecipt} ref={ref} alt="check recipt" className={cn(IconVariants({ size, className }))} {...props} />
));

CheckRecipt.displayName = "CheckRecipt";

export const CheckSquare = React.forwardRef(({ className, size, ...props }, ref) => (
  <Image src={checkSquare} ref={ref} alt="check square" className={cn(IconVariants({ size, className }))} {...props} />
));

CheckSquare.displayName = "CheckSquare";

export const Address = React.forwardRef(({ className, size, ...props }, ref) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={cn(IconVariants({ size, className }))}
    {...props}
  >
    <path
      d="M15 1.875C16.2188 1.875 17.2153 2.82642 17.2875 4.02706L17.2917 4.16667V7.5C17.2917 7.84518 17.0118 8.125 16.6667 8.125C16.3502 8.125 16.0888 7.88987 16.0474 7.58481L16.0417 7.5V4.16667C16.0417 3.62733 15.6318 3.18372 15.1065 3.13038L15 3.125H4.16667C3.62733 3.125 3.18372 3.53489 3.13038 4.06016L3.125 4.16667V15.8333C3.125 16.3727 3.53489 16.8163 4.06016 16.8696L4.16667 16.875H6.66666C7.01184 16.875 7.29166 17.1548 7.29166 17.5C7.29166 17.8164 7.05654 18.0779 6.75147 18.1193L6.66666 18.125H4.16667C2.94789 18.125 1.95133 17.1736 1.87918 15.9729L1.875 15.8333V4.16667C1.875 2.94789 2.82642 1.95133 4.02706 1.87918L4.16667 1.875H15Z"
      fill="currentColor"
    />
    <path
      d="M13.333 5.20898C13.6782 5.20898 13.958 5.48881 13.958 5.83398C13.958 6.1504 13.7229 6.41189 13.4178 6.45328L13.333 6.45898H5.83301C5.48783 6.45898 5.20801 6.17916 5.20801 5.83398C5.20801 5.51757 5.44314 5.25608 5.7482 5.21469L5.83301 5.20898H13.333Z"
      fill="currentColor"
    />
    <path
      d="M8.33301 8.54102C8.67818 8.54102 8.95801 8.82084 8.95801 9.16602C8.95801 9.48243 8.72288 9.74392 8.41782 9.78531L8.33301 9.79102H5.83301C5.48783 9.79102 5.20801 9.51119 5.20801 9.16602C5.20801 8.8496 5.44314 8.58811 5.7482 8.54672L5.83301 8.54102H8.33301Z"
      fill="currentColor"
    />
    <path
      d="M6.66634 11.875C7.01152 11.875 7.29134 12.1548 7.29134 12.5C7.29134 12.8164 7.05621 13.0779 6.75115 13.1193L6.66634 13.125H5.83301C5.48783 13.125 5.20801 12.8452 5.20801 12.5C5.20801 12.1836 5.44314 11.9221 5.7482 11.8807L5.83301 11.875H6.66634Z"
      fill="currentColor"
    />
    <path
      d="M17.5 11.25C17.8164 11.25 18.0779 11.4851 18.1193 11.7902L18.125 11.875V17.0833C18.125 17.6227 17.7151 18.0663 17.1898 18.1196L17.0833 18.125H10.4167C9.87733 18.125 9.43372 17.7151 9.38038 17.1898L9.375 17.0833V11.875C9.375 11.5298 9.65482 11.25 10 11.25C10.3164 11.25 10.5779 11.4851 10.6193 11.7902L10.625 11.875V16.875H16.875V11.875C16.875 11.5586 17.1101 11.2971 17.4152 11.2557L17.5 11.25Z"
      fill="currentColor"
    />
    <path
      d="M14.5413 13.541C15.0807 13.541 15.5243 13.9509 15.5776 14.4762L15.583 14.5827V17.4993C15.583 17.8445 15.3032 18.1243 14.958 18.1243C14.6416 18.1243 14.3801 17.8892 14.3387 17.5842L14.333 17.4993V14.791H13.083V17.4993C13.083 17.8158 12.8479 18.0773 12.5428 18.1186L12.458 18.1243C12.1416 18.1243 11.8801 17.8892 11.8387 17.5842L11.833 17.4993V14.5827C11.833 14.0433 12.2429 13.5997 12.7682 13.5464L12.8747 13.541H14.5413Z"
      fill="currentColor"
    />
    <path
      d="M13.1254 8.74935C13.4649 8.49472 13.9211 8.4735 14.28 8.68569L14.3754 8.74935L18.7087 11.9993C18.9848 12.2065 19.0408 12.5982 18.8337 12.8743C18.6454 13.1254 18.3045 13.1945 18.0366 13.0493L17.9587 12.9993L13.7504 9.84352L9.54203 12.9993C9.29099 13.1876 8.94441 13.1585 8.72786 12.9442L8.66703 12.8743C8.47875 12.6233 8.50789 12.2767 8.72221 12.0602L8.79203 11.9993L13.1254 8.74935Z"
      fill="currentColor"
    />
  </svg>
));

Address.displayName = "Address";

export const Identification = React.forwardRef(({ className, size, ...props }, ref) => (
  <Image
    src={identification}
    ref={ref}
    alt="identification"
    className={cn(IconVariants({ size, className }))}
    {...props}
  />
));

Identification.displayName = "Identification";

export const Suitcase = React.forwardRef(({ className, size, ...props }, ref) => (
  <Image src={suitcase} ref={ref} alt="suitcase" className={cn(IconVariants({ size, className }))} {...props} />
));

Suitcase.displayName = "Suitcase";

Identification.displayName = "Identification";

export const Lock = React.forwardRef(({ className, size, ...props }, ref) => (
  <Image src={lock} ref={ref} alt="lock" className={cn(IconVariants({ size, className }))} {...props} />
));

Lock.displayName = "Lock";

Identification.displayName = "Identification";

export const Link = React.forwardRef(({ className, size, ...props }, ref) => (
  <Image src={link} ref={ref} alt="link" className={cn(IconVariants({ size, className }))} {...props} />
));

Link.displayName = "Link";

Identification.displayName = "Identification";

export const Deactiveuser = React.forwardRef(({ className, size, ...props }, ref) => (
  <Image
    src={deactiveuser}
    ref={ref}
    alt="deactive user"
    className={cn(IconVariants({ size, className }))}
    {...props}
  />
));

Deactiveuser.displayName = "Deactiveuser";

Identification.displayName = "Identification";

export const Home = React.forwardRef(({ className, size, ...props }, ref) => (
  <Image src={home} ref={ref} alt="home" className={cn(IconVariants({ size, className }))} {...props} />
));

Home.displayName = "Home";

Identification.displayName = "Identification";

export const Pointer = React.forwardRef(({ className, size, ...props }, ref) => (
  <Image src={pointer} ref={ref} alt="pointer" className={cn(IconVariants({ size, className }))} {...props} />
));

Pointer.displayName = "Pointer";

Identification.displayName = "Identification";

export const Threedot = React.forwardRef(({ className, size, ...props }, ref) => (
  <Image src={threedot} ref={ref} alt="threedot" className={cn(IconVariants({ size, className }))} {...props} />
));

Threedot.displayName = "Threedot";

export const Customersupport = React.forwardRef(({ className, size, ...props }, ref) => (
  <Image
    src={customersupport}
    ref={ref}
    alt="customersupport"
    className={cn(IconVariants({ size, className }))}
    {...props}
  />
));

Customersupport.displayName = "Customersupport";

export const Specialrequests = React.forwardRef(({ className, size, ...props }, ref) => (
  <Image
    src={specialrequests}
    ref={ref}
    alt="specialrequests"
    className={cn(IconVariants({ size, className }))}
    {...props}
  />
));

Specialrequests.displayName = "Specialrequests";

export const Personalizedservice = React.forwardRef(({ className, size, ...props }, ref) => (
  <Image
    src={personalizedservice}
    ref={ref}
    alt="personalizedservice"
    className={cn(IconVariants({ size, className }))}
    {...props}
  />
));

Personalizedservice.displayName = "Personalizedservice";

export const Addresspoint = React.forwardRef(({ className, size, ...props }, ref) => (
  <Image src={addresspoint} ref={ref} alt="addresspoint" className={cn(IconVariants({ size, className }))} {...props} />
));

Addresspoint.displayName = "Addresspoint";

Identification.displayName = "Identification";

export const Comment = React.forwardRef(({ className, size, ...props }, ref) => (
  <Image src={comment} ref={ref} alt="comment" className={cn(IconVariants({ size, className }))} {...props} />
));

Comment.displayName = "Comment";

export const Truck = React.forwardRef(({ className, size }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="35"
    height="35"
    viewBox="0 0 35 35"
    fill="none"
    className={cn(IconVariants({ size, className }))}
  >
    <path
      d="M23.0555 25.9463C23.6595 25.9463 24.1492 26.436 24.1492 27.04C24.1492 27.5938 23.7377 28.0514 23.2039 28.1238L23.0555 28.1338H11.6367C11.0327 28.1338 10.543 27.6441 10.543 27.04C10.543 26.4863 10.9544 26.0287 11.4883 25.9563L11.6367 25.9463H23.0555Z"
      fill="currentColor"
    />
    <path
      d="M27.424 28.1338C26.8199 28.1338 26.3302 27.6441 26.3302 27.0401C26.3302 26.4863 26.7417 26.0287 27.2755 25.9563L27.424 25.9463H30.326C30.4958 25.9463 30.6424 25.8239 30.6808 25.6643L30.6906 25.5817V16.6567C30.6906 16.4807 30.6657 16.312 30.6128 16.1416L30.5505 15.9702L28.5695 11.0323C28.3068 10.3882 27.7234 9.9529 27.0542 9.88483L26.8698 9.87549H21.5469C20.9428 9.87549 20.4531 9.3858 20.4531 8.78174C20.4531 8.22802 20.8646 7.7704 21.3985 7.69797L21.5469 7.68799H26.8698C28.4171 7.68799 29.8108 8.57664 30.4859 9.96238L30.5974 10.212L32.5752 15.1423C32.7363 15.529 32.8335 15.9311 32.866 16.3446L32.8781 16.6567V25.5817C32.8781 26.9176 31.8439 28.0186 30.5349 28.1253L30.326 28.1338H27.424Z"
      fill="currentColor"
    />
    <path
      d="M30.6237 12.0762C31.2278 12.0762 31.7174 12.5659 31.7174 13.1699C31.7174 13.7236 31.306 14.1813 30.7721 14.2537L30.6237 14.2637L27.0505 14.2634V17.9253L31.805 17.9255C32.3587 17.9255 32.8163 18.337 32.8887 18.8709L32.8987 19.0193C32.8987 19.573 32.4872 20.0306 31.9534 20.1031L31.805 20.113L26.6472 20.1124C25.7154 20.0791 24.9628 19.373 24.8724 18.4606L24.8633 18.2755V13.1699C24.8633 12.6162 25.2748 12.1586 25.8086 12.0862L25.957 12.0762H30.6237Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M27.4998 24.6558C26.2082 23.3861 24.1303 23.3928 22.8614 24.6836L22.7637 24.7966C22.7343 24.8354 22.708 24.8753 22.6848 24.916L22.6728 24.9397L22.5483 25.1014C21.6202 26.391 21.7363 28.1983 22.8972 29.3592C24.1898 30.63 26.2576 30.63 27.5436 29.3658L27.7175 29.1749C28.7641 27.9351 28.7644 26.1242 27.7233 24.9077L27.6166 24.7924L27.556 24.7152L27.4998 24.6558ZM24.6736 26.027C25.0376 25.8296 25.4955 25.8566 25.842 26.1101L25.9381 26.1924L26.1075 26.3885C26.4197 26.812 26.3845 27.4247 25.9969 27.819C25.5752 28.2333 24.8722 28.2333 24.4373 27.8058C24.0107 27.3791 24.0085 26.6812 24.436 26.2463L24.5526 26.1035L24.6736 26.027Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.2742 24.5018C9.97831 23.3914 8.03118 23.4501 6.81847 24.6837L6.72073 24.7967C6.69133 24.8355 6.66506 24.8754 6.64184 24.9161L6.62988 24.9398L6.50529 25.1015C5.57728 26.3911 5.69335 28.1984 6.85422 29.3593C8.14685 30.6301 10.2146 30.6301 11.5006 29.3659L11.6746 29.175C12.7763 27.87 12.7186 25.9321 11.5073 24.7208L11.2742 24.5018ZM9.91672 26.2239L9.9403 26.245L10.0645 26.3886C10.3767 26.8122 10.3415 27.4248 9.95389 27.8191C9.53227 28.2334 8.82922 28.2334 8.39438 27.8059C7.96777 27.3793 7.9655 26.6813 8.39302 26.2464L8.50967 26.1036L8.63018 26.0274C9.03841 25.806 9.56312 25.8703 9.91672 26.2239Z"
      fill="currentColor"
    />
    <path
      d="M20.1107 6.94434H4.06899C3.9011 6.94434 3.76113 7.06193 3.72692 7.23351L3.71899 7.3235V25.567L3.72839 25.6558C3.75796 25.7912 3.8547 25.8939 3.98249 25.9312L4.08357 25.9464H7.01482C7.61889 25.9464 8.10857 26.4361 8.10857 27.0402C8.10857 27.5939 7.6971 28.0515 7.16324 28.1239L7.01482 28.1339L4.06405 28.1337C2.72577 28.1098 1.65019 27.0965 1.54068 25.788L1.53149 25.5673L1.53167 7.34303C1.5075 5.98947 2.54009 4.87363 3.85855 4.76543L4.06899 4.75684L20.1294 4.75951L20.2863 4.76186C21.5341 4.84244 22.5374 5.83812 22.6396 7.08441L22.6482 7.29434V20.4193C22.6482 21.0234 22.1585 21.5131 21.5544 21.5131C21.0007 21.5131 20.5431 21.1016 20.4706 20.5678L20.4607 20.4193V7.29434C20.4607 7.13162 20.35 6.99458 20.2058 6.95536L20.1107 6.94434Z"
      fill="currentColor"
    />
    <path
      d="M21.599 19.3838C22.203 19.3838 22.6927 19.8735 22.6927 20.4775C22.6927 21.0313 22.2812 21.4889 21.7474 21.5613L21.599 21.5713H2.64062C2.03656 21.5713 1.54688 21.0816 1.54688 20.4775C1.54688 19.9238 1.95835 19.4662 2.49221 19.3938L2.64062 19.3838H21.599Z"
      fill="currentColor"
    />
  </svg>
));

Truck.displayName = "Truck";

export const ComputerDollar = React.forwardRef(({ className, size, ...props }, ref) => (
  <>
    <svg
      width="37"
      height="35"
      viewBox="0 0 37 35"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn(IconVariants({ size, className }))}
    >
      <g id="Group">
        <path
          id="Path"
          d="M23.6654 6.83569C24.3557 6.83569 24.9154 7.39534 24.9154 8.08569C24.9154 8.77605 24.3557 9.33569 23.6654 9.33569H20.332C19.6417 9.33569 19.082 8.77605 19.082 8.08569C19.082 7.39534 19.6417 6.83569 20.332 6.83569H23.6654Z"
          fill="currentColor"
        />
        <path
          id="Path_2"
          d="M23.6667 11.8357C24.357 11.8357 24.9167 12.3953 24.9167 13.0857C24.9167 13.776 24.357 14.3357 23.6667 14.3357H22C21.3096 14.3357 20.75 13.776 20.75 13.0857C20.75 12.3953 21.3096 11.8357 22 11.8357H23.6667Z"
          fill="currentColor"
        />
        <path
          id="Path_3"
          d="M32 0.168945C32.6904 0.168945 33.25 0.728589 33.25 1.41895V2.76538C33.25 3.45573 32.6904 4.01538 32 4.01538C31.3096 4.01538 30.75 3.45573 30.75 2.76538V1.41895C30.75 0.728589 31.3096 0.168945 32 0.168945Z"
          fill="currentColor"
        />
        <path
          id="Path_4"
          d="M31.8406 1.50352L32.1195 1.52103C32.8067 1.58623 33.311 2.19623 33.2458 2.8835C33.1806 3.57077 32.5706 4.07506 31.8834 4.00986C30.9014 3.9167 30.0248 4.62485 29.9095 5.60445C29.8288 6.29007 29.2076 6.78045 28.522 6.69974C27.8363 6.61903 27.346 5.99779 27.4267 5.31217C27.7008 2.98319 29.7849 1.29955 32.1195 1.52103L31.8406 1.50352Z"
          fill="currentColor"
        />
        <path
          id="Path_5"
          d="M32 12.1558C32.6904 12.1558 33.25 12.7154 33.25 13.4058V14.7522C33.25 15.4426 32.6904 16.0022 32 16.0022C31.3096 16.0022 30.75 15.4426 30.75 14.7522V13.4058C30.75 12.7154 31.3096 12.1558 32 12.1558Z"
          fill="currentColor"
        />
        <path
          id="Path_6"
          d="M35.3098 9.46309L35.4796 9.47158C36.1652 9.5523 36.6556 10.1735 36.5748 10.8592C36.3007 13.1881 34.2166 14.8717 31.882 14.6503C31.1948 14.5851 30.6905 13.9751 30.7557 13.2878C30.8209 12.6005 31.4309 12.0962 32.1181 12.1614C33.1001 12.2546 33.9767 11.5464 34.092 10.5669C34.1727 9.88124 34.7939 9.39087 35.4796 9.47158L35.3098 9.46309Z"
          fill="currentColor"
        />
        <path
          id="Path_7"
          d="M31.9218 1.51791C33.8991 1.39398 35.7249 2.57735 36.4192 4.43281C36.6612 5.07938 36.3332 5.79966 35.6866 6.04161C35.04 6.28356 34.3197 5.95556 34.0778 5.30899C33.7691 4.48405 32.9573 3.95792 32.0782 4.01302C31.3892 4.0562 30.7957 3.53266 30.7525 2.84366C30.7093 2.15466 31.2328 1.5611 31.9218 1.51791Z"
          fill="currentColor"
        />
        <path
          id="Path_8"
          d="M28.3122 10.1297C28.9588 9.88771 29.6791 10.2157 29.921 10.8623C30.2298 11.6872 31.0415 12.2133 31.9206 12.1582C32.6096 12.115 33.2031 12.6385 33.2463 13.3275C33.2895 14.0165 32.766 14.6101 32.077 14.6533C30.0998 14.7772 28.274 13.5939 27.5796 11.7385C27.3377 11.092 27.6657 10.3717 28.3122 10.1297Z"
          fill="currentColor"
        />
        <path
          id="Path_9"
          d="M28.668 4.20825C29.3583 4.20825 29.918 4.7679 29.918 5.45825C29.918 6.00247 30.3021 6.47103 30.8357 6.57776L33.6572 7.14204C35.3594 7.48249 36.5846 8.97708 36.5846 10.713C36.5846 11.4033 36.025 11.963 35.3346 11.963C34.6443 11.963 34.0846 11.4033 34.0846 10.713C34.0846 10.1688 33.7005 9.70022 33.1669 9.59349L30.3454 9.02921C28.6432 8.68877 27.418 7.19417 27.418 5.45825C27.418 4.7679 27.9776 4.20825 28.668 4.20825Z"
          fill="currentColor"
        />
        <path
          id="Path_10"
          d="M23.668 1.78076C24.3583 1.78076 24.918 2.34041 24.918 3.03076C24.918 3.72112 24.3583 4.28076 23.668 4.28076H18.668C17.9776 4.28076 17.418 3.72112 17.418 3.03076C17.418 2.34041 17.9776 1.78076 18.668 1.78076H23.668Z"
          fill="currentColor"
        />
        <path
          id="Path_11"
          d="M20.7229 25.9556C21.4119 25.9125 22.0053 26.4362 22.0484 27.1252L22.4126 32.9528C22.4556 33.6419 21.932 34.2353 21.243 34.2784C20.554 34.3214 19.9605 33.7978 19.9174 33.1088L19.5533 27.2811C19.5102 26.5921 20.0338 25.9987 20.7229 25.9556Z"
          fill="currentColor"
        />
        <path
          id="Path_12"
          d="M13.2743 25.9556C13.9634 25.9987 14.487 26.5921 14.4439 27.2812L14.0796 33.1088C14.0366 33.7978 13.4431 34.3214 12.7541 34.2784C12.0651 34.2353 11.5414 33.6418 11.5845 32.9528L11.9488 27.1252C11.9919 26.4362 12.5853 25.9125 13.2743 25.9556Z"
          fill="currentColor"
        />
        <path
          id="Path_13"
          d="M23.6654 31.7808C24.3557 31.7808 24.9154 32.3404 24.9154 33.0308C24.9154 33.7211 24.3557 34.2808 23.6654 34.2808H10.332C9.64168 34.2808 9.08203 33.7211 9.08203 33.0308C9.08203 32.3404 9.64168 31.7808 10.332 31.7808H23.6654Z"
          fill="currentColor"
        />
        <path
          id="Path_14"
          d="M13.6667 1.78638C14.357 1.78638 14.9167 2.34602 14.9167 3.03638C14.9167 3.72673 14.357 4.28638 13.6667 4.28638H5.33333C4.18274 4.28638 3.25 5.21912 3.25 6.36971V23.8697C3.25 25.0203 4.18274 25.953 5.33333 25.953H28.6667C29.8173 25.953 30.75 25.0203 30.75 23.8697V19.6974C30.75 19.007 31.3096 18.4474 32 18.4474C32.6904 18.4474 33.25 19.007 33.25 19.6974V23.8697C33.25 26.401 31.198 28.453 28.6667 28.453H5.33333C2.80203 28.453 0.75 26.401 0.75 23.8697V6.36971C0.75 3.83841 2.80203 1.78638 5.33333 1.78638H13.6667Z"
          fill="currentColor"
        />
      </g>
    </svg>
  </>
));

ComputerDollar.displayName = "ComputerDollar";

export const ShoppingBagSearch = React.forwardRef(({ className, size, ...props }, ref) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="40"
    height="41"
    viewBox="0 0 40 41"
    fill="none"
    className={cn(IconVariants({ size, className }))}
  >
    <g id="Group">
      <g id="Group_2">
        <path
          id="Path"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M32.7541 24.111C30.3143 21.6691 26.3559 21.6691 23.9143 24.1107C21.1724 26.8526 21.5465 31.4088 24.7771 33.6746C26.8794 35.1502 29.7887 35.1502 31.8913 33.6743C35.1205 31.4062 35.4951 26.852 32.7541 24.111ZM25.6821 25.8784C27.0775 24.4831 29.298 24.4166 30.7706 25.6788L30.986 25.8784L31.1747 26.0811C32.6123 27.7417 32.3303 30.3109 30.4547 31.6283C29.2144 32.4989 27.4537 32.4989 26.213 31.6281C24.2588 30.2575 24.0345 27.526 25.6821 25.8784Z"
          fill="currentColor"
        />
        <path
          id="Path_2"
          d="M30.9833 31.1791C31.4271 30.7353 32.1215 30.695 32.6109 31.0581L32.7511 31.1791L35.8844 34.3124C36.3726 34.8006 36.3726 35.592 35.8844 36.0802C35.4406 36.524 34.7462 36.5643 34.2568 36.2012L34.1166 36.0802L30.9833 32.9469C30.4951 32.4587 30.4951 31.6673 30.9833 31.1791Z"
          fill="currentColor"
        />
        <path
          id="Path_3"
          d="M19.7318 3.94629C23.2535 3.94629 26.1302 6.7129 26.3065 10.1909L26.3151 10.5296V13.5296C26.3151 14.22 25.7555 14.7796 25.0651 14.7796C24.4323 14.7796 23.9093 14.3094 23.8265 13.6992L23.8151 13.5296V10.5296C23.8151 8.27498 21.9864 6.44629 19.7318 6.44629C17.5673 6.44629 15.7954 8.13161 15.6571 10.2612L15.6484 10.5296V13.5296C15.6484 14.22 15.0888 14.7796 14.3984 14.7796C13.7656 14.7796 13.2426 14.3094 13.1598 13.6992L13.1484 13.5296V10.5296C13.1484 6.89427 16.0964 3.94629 19.7318 3.94629Z"
          fill="currentColor"
        />
        <path
          id="Path_4"
          d="M29.858 12.2795C31.298 12.2795 32.5077 13.3256 32.736 14.722L32.7659 14.9719L33.0326 18.4335C33.0856 19.1218 32.5706 19.7228 31.8823 19.7758C31.2514 19.8245 30.6938 19.3958 30.5644 18.7938L30.54 18.6255L30.2733 15.1636C30.2594 14.9827 30.1319 14.8366 29.9634 14.7929L29.858 14.7795H9.6113C9.4293 14.7795 9.27385 14.8955 9.21738 15.0608L9.19596 15.1654L7.92431 31.7034C7.83704 32.8419 8.67927 33.8241 9.79005 33.9357L10.0013 33.9462H20.0013C20.6917 33.9462 21.2513 34.5059 21.2513 35.1962C21.2513 35.829 20.781 36.352 20.1709 36.4348L20.0013 36.4462H10.0013C7.42727 36.4462 5.37865 34.333 5.41854 31.7956L5.43165 31.512L6.70329 14.974C6.81338 13.5374 7.94895 12.411 9.35965 12.2903L9.6113 12.2795H29.858Z"
          fill="currentColor"
        />
      </g>
    </g>
  </svg>
));

ShoppingBagSearch.displayName = "ShoppingBagSearch";

export const Trust = React.forwardRef(({ className, size }) => (
  <>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="34"
      height="36"
      viewBox="0 0 34 36"
      fill="none"
      className={cn(IconVariants({ size, className }))}
    >
      <g id="Group">
        <path
          id="Path"
          d="M26.1662 29.627C26.8565 29.627 27.4162 30.1866 27.4162 30.877C27.4162 31.5673 26.8565 32.127 26.1662 32.127H24.4057C24.1078 32.127 23.8134 32.1908 23.5422 32.3143L16.5301 35.5079C15.9018 35.794 15.1606 35.5166 14.8744 34.8884C14.5883 34.2601 14.8657 33.5188 15.4939 33.2327L22.506 30.0392C23.1025 29.7675 23.7503 29.627 24.4057 29.627H26.1662Z"
          fill="currentColor"
        />
        <path
          id="Path_2"
          d="M11.2936 19.39C12.5677 18.9401 13.9757 19.078 15.1383 19.7665C15.7324 20.1182 15.9288 20.8849 15.577 21.4789C15.2253 22.073 14.4586 22.2694 13.8645 21.9176C13.3388 21.6063 12.7021 21.544 12.1331 21.7448L8.2948 23.1265C7.64524 23.3603 6.92913 23.0233 6.69531 22.3737C6.46149 21.7242 6.79852 21.0081 7.44807 20.7743L11.2936 19.39Z"
          fill="currentColor"
        />
        <path
          id="Path_3"
          d="M17.292 17.55C18.5874 16.7108 20.2181 16.5798 21.6308 17.2017L26.6722 19.4209C27.304 19.699 27.5908 20.4367 27.3126 21.0685C27.0345 21.7004 26.2968 21.9871 25.665 21.709L20.6235 19.4898C19.9814 19.2072 19.2402 19.2667 18.6514 19.6481L14.3014 22.4665C13.9732 22.6791 13.7623 23.032 13.7307 23.4217C13.6991 23.8114 13.8503 24.1937 14.1394 24.4559C14.5154 24.7965 15.059 24.8786 15.5183 24.6646L18.0625 23.4775C18.6881 23.1855 19.4319 23.4561 19.7238 24.0817C20.0157 24.7073 19.7452 25.4511 19.1196 25.743L16.5749 26.9303C15.2034 27.5695 13.5825 27.3246 12.4605 26.3083C11.5959 25.5242 11.1445 24.3829 11.2389 23.2195C11.3333 22.0561 11.9627 21.0026 12.9422 20.3683L17.292 17.55Z"
          fill="currentColor"
        />
        <path
          id="Path_4"
          d="M17.649 23.7872C18.1035 23.2675 18.8932 23.2147 19.4128 23.6693L20.8391 24.9168C21.8206 25.7753 22.39 27.0112 22.4046 28.315C22.4193 29.6189 21.8779 30.8673 20.916 31.7477L17.6055 34.7775C15.8599 36.3752 13.1849 36.3813 11.432 34.7915L9.13247 32.7059C8.78996 32.4012 8.35634 32.2182 7.89907 32.1855C7.21048 32.1362 6.69223 31.538 6.74153 30.8494C6.79082 30.1608 7.389 29.6426 8.0776 29.6919C9.08475 29.764 10.0398 30.167 10.8031 30.8461L13.1115 32.9397C13.9083 33.6623 15.1242 33.6595 15.9176 32.9333L19.2282 29.9035C19.6654 29.5033 19.9115 28.9359 19.9048 28.3432C19.8981 27.7505 19.6393 27.1887 19.1932 26.7985L17.7669 25.551C17.2473 25.0965 17.1945 24.3068 17.649 23.7872Z"
          fill="currentColor"
        />
        <path
          id="Path_5"
          d="M6.16667 18.4797C7.7775 18.4797 9.08333 19.7856 9.08333 21.3964V31.3964C9.08333 33.0072 7.7775 34.3131 6.16667 34.3131H2C1.30964 34.3131 0.75 33.7534 0.75 33.0631C0.75 32.3727 1.30964 31.8131 2 31.8131H6.16667C6.39679 31.8131 6.58333 31.6265 6.58333 31.3964V21.3964C6.58333 21.1663 6.39679 20.9797 6.16667 20.9797H2C1.30964 20.9797 0.75 20.4201 0.75 19.7297C0.75 19.0394 1.30964 18.4797 2 18.4797H6.16667Z"
          fill="currentColor"
        />
        <path
          id="Path_6"
          d="M32.0013 18.4797C32.6917 18.4797 33.2513 19.0394 33.2513 19.7297C33.2513 20.4201 32.6917 20.9797 32.0013 20.9797H27.8346C27.6045 20.9797 27.418 21.1663 27.418 21.3964V31.3964C27.418 31.6265 27.6045 31.8131 27.8346 31.8131H32.0013C32.6917 31.8131 33.2513 32.3727 33.2513 33.0631C33.2513 33.7534 32.6917 34.3131 32.0013 34.3131H27.8346C26.2238 34.3131 24.918 33.0072 24.918 31.3964V21.3964C24.918 19.7856 26.2238 18.4797 27.8346 18.4797H32.0013Z"
          fill="currentColor"
        />
        <path
          id="Path_7"
          d="M28.25 0.146484C31.0114 0.146484 33.25 2.38506 33.25 5.14648V14.7298C33.25 15.4202 32.6904 15.9798 32 15.9798C31.3096 15.9798 30.75 15.4202 30.75 14.7298V5.14648C30.75 3.76577 29.6307 2.64648 28.25 2.64648H5.75C4.36929 2.64648 3.25 3.76577 3.25 5.14648V14.7298C3.25 15.4202 2.69036 15.9798 2 15.9798C1.30964 15.9798 0.75 15.4202 0.75 14.7298V5.14648C0.75 2.38506 2.98858 0.146484 5.75 0.146484H28.25Z"
          fill="currentColor"
        />
        <path
          id="Path_8"
          d="M7.36612 9.2626C7.85427 8.77445 8.64573 8.77445 9.13388 9.2626L11.2172 11.3459C11.7054 11.8341 11.7054 12.6255 11.2172 13.1137C10.7291 13.6019 9.9376 13.6019 9.44945 13.1137L7.36612 11.0304C6.87796 10.5422 6.87796 9.75076 7.36612 9.2626Z"
          fill="currentColor"
        />
        <path
          id="Path_9"
          d="M12.7815 8.0126C13.2696 7.52445 14.0611 7.52445 14.5492 8.0126C15.0374 8.50076 15.0374 9.29221 14.5492 9.78037L11.2159 13.1137C10.7278 13.6019 9.9363 13.6019 9.44815 13.1137C8.95999 12.6255 8.95999 11.8341 9.44815 11.3459L12.7815 8.0126Z"
          fill="currentColor"
        />
        <path
          id="Path_10"
          d="M25.332 10.1465C26.0224 10.1465 26.582 10.7061 26.582 11.3965C26.582 12.0868 26.0224 12.6465 25.332 12.6465H17.832C17.1417 12.6465 16.582 12.0868 16.582 11.3965C16.582 10.7061 17.1417 10.1465 17.832 10.1465H25.332Z"
          fill="currentColor"
        />
      </g>
    </svg>
  </>
));

Trust.displayName = "Trust";

export const PinGlobe = React.forwardRef(({ className, size, ...props }, ref) => (
  <>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="41"
      viewBox="0 0 40 41"
      fill="none"
      className={cn(IconVariants({ size, className }))}
    >
      <path
        d="M20 4.13257C28.9746 4.13257 36.25 11.4079 36.25 20.3826C36.25 21.0729 35.6904 21.6326 35 21.6326C34.3096 21.6326 33.75 21.0729 33.75 20.3826C33.75 12.7887 27.5939 6.63257 20 6.63257C12.4061 6.63257 6.25 12.7887 6.25 20.3826C6.25 27.9765 12.4061 34.1326 20 34.1326C20.6904 34.1326 21.25 34.6922 21.25 35.3826C21.25 36.0729 20.6904 36.6326 20 36.6326C11.0254 36.6326 3.75 29.3572 3.75 20.3826C3.75 11.4079 11.0254 4.13257 20 4.13257Z"
        fill="currentColor"
      />
      <path
        d="M23.3362 4.50708C23.9691 4.50708 24.4921 4.97734 24.5748 5.58746L24.5862 5.75708V12.0493C24.5862 14.4868 22.6834 16.48 20.2821 16.6243L20.0029 16.6326H16.6696C15.5909 16.6326 14.7037 17.4524 14.597 18.5029L14.5862 18.7159V22.0493C14.5862 24.4868 12.6834 26.48 10.2821 26.6243L10.0029 26.6326H5.85938C5.16902 26.6326 4.60938 26.073 4.60938 25.3826C4.60938 24.7498 5.07963 24.2268 5.68976 24.144L5.85938 24.1326H10.0029C11.0816 24.1326 11.9688 23.3128 12.0755 22.2623L12.0862 22.0493V18.7159C12.0862 16.2784 13.9891 14.2853 16.3904 14.141L16.6696 14.1326H20.0029C21.0816 14.1326 21.9688 13.3128 22.0755 12.2623L22.0862 12.0493V5.75708C22.0862 5.06672 22.6459 4.50708 23.3362 4.50708Z"
        fill="currentColor"
      />
      <path
        d="M29.1533 27.8833L29.3743 27.8959C29.6659 27.9322 29.9428 28.0451 30.1766 28.223L30.3435 28.3684C30.6593 28.6829 30.8357 29.1109 30.8333 29.5499C30.8333 30.4695 30.0885 31.2154 29.1689 31.2166C28.2493 31.2178 27.5025 30.474 27.5 29.5543C27.4977 28.7004 28.1381 27.9946 28.9659 27.8952L29.1533 27.8833Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M28.8155 22.4756L29.1693 22.4666C32.9367 22.4666 36.0395 25.4154 36.2435 29.1961L36.2526 29.5499C36.2526 32.2945 34.1707 35.0145 31.1112 37.6103C29.9804 38.5297 28.3598 38.5297 27.2093 37.594C24.3595 35.1781 22.3348 32.6076 22.1024 29.9476L22.0859 29.5499C22.0859 25.7824 25.0348 22.6796 28.8155 22.4756ZM29.1855 24.9648L28.9179 24.9733C26.4956 25.1043 24.5872 27.1124 24.5859 29.4943L24.5976 29.7827C24.7491 31.5073 26.4618 33.6817 28.8074 35.6706C29.0194 35.8429 29.3233 35.8429 29.5151 35.6873C32.0075 33.5724 33.7538 31.2907 33.7543 29.5835L33.747 29.2973C33.616 26.8749 31.608 24.9665 29.1855 24.9648Z"
        fill="currentColor"
      />
    </svg>
  </>
));

PinGlobe.displayName = "PinGlobe";

export const Chat = React.forwardRef(({ className, size }) => (
  <>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="21"
      viewBox="0 0 20 21"
      fill="none"
      className={cn(IconVariants({ size, className }))}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5 3.10107H15C16.726 3.10107 18.125 4.50006 18.125 6.22607V12.8927C18.125 14.6188 16.726 16.0177 15 16.0177H10.1892L6.59669 18.4128C6.20577 18.6734 5.68996 18.425 5.63063 17.9787L5.625 17.8927V16.0177H5C3.32967 16.0177 1.9656 14.7076 1.87933 13.0587L1.875 12.8927V6.22607C1.875 4.50006 3.27399 3.10107 5 3.10107ZM15 4.35107H5C3.96434 4.35107 3.125 5.19042 3.125 6.22607V12.8927C3.125 13.9284 3.96434 14.7677 5 14.7677H6.25C6.59518 14.7677 6.875 15.0476 6.875 15.3927V16.7236L9.65331 14.8727C9.73031 14.8214 9.81742 14.788 9.90821 14.7745L10 14.7677H15C16.0357 14.7677 16.875 13.9284 16.875 12.8927V6.22607C16.875 5.19042 16.0357 4.35107 15 4.35107Z"
        fill="currentColor"
      />
    </svg>
  </>
));

Chat.displayName = "Chat";

export const ShoppingBag = React.forwardRef(({ className, size, ...props }, ref) => (
  <>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="19"
      viewBox="0 0 18 19"
      fill="none"
      className={cn(IconVariants({ size, className }))}
    >
      <path
        d="M12.2502 6.83838C12.635 6.83838 12.947 7.15031 12.947 7.5351C12.947 9.71557 11.1793 11.4832 8.99887 11.4832C6.8184 11.4832 5.05078 9.71557 5.05078 7.5351C5.05078 7.15031 5.36271 6.83838 5.7475 6.83838C6.13229 6.83838 6.44423 7.15031 6.44423 7.5351C6.44423 8.94599 7.58798 10.0897 8.99887 10.0897C10.4098 10.0897 11.5535 8.94599 11.5535 7.5351C11.5535 7.15031 11.8655 6.83838 12.2502 6.83838Z"
        fill="currentColor"
      />
      <path
        d="M8.99887 0.335449C11.1152 0.335449 12.8426 2.00062 12.9424 4.09225L12.947 4.28354V4.74802C12.947 5.13281 12.635 5.44474 12.2502 5.44474C11.8975 5.44474 11.606 5.18263 11.5599 4.84256L11.5535 4.74802V4.28354C11.5535 2.87265 10.4098 1.72889 8.99887 1.72889C7.64024 1.72889 6.52931 2.78949 6.44889 4.12792L6.44423 4.28354V4.74802C6.44423 5.13281 6.13229 5.44474 5.7475 5.44474C5.39478 5.44474 5.10328 5.18263 5.05714 4.84256L5.05078 4.74802V4.28354C5.05078 2.10307 6.8184 0.335449 8.99887 0.335449Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.85662 4.05127H14.142C15.491 4.05127 16.6075 5.10018 16.6917 6.44656L17.1181 13.2689C17.2021 14.6126 16.7269 15.9312 15.805 16.9124C14.8832 17.8937 13.5968 18.4502 12.2505 18.4502H5.74811C4.40182 18.4502 3.11541 17.8937 2.19361 16.9124C1.27181 15.9312 0.796623 14.6126 0.880601 13.2689L1.307 6.44656C1.39115 5.10019 2.50763 4.05127 3.85662 4.05127ZM14.1418 5.4447H3.85639C3.24324 5.4447 2.73575 5.92148 2.6975 6.53346L2.27111 13.3558C2.21112 14.3156 2.55054 15.2575 3.20897 15.9583C3.8674 16.6592 4.78626 17.0567 5.74788 17.0567H12.2503C13.2119 17.0567 14.1308 16.6592 14.7892 15.9583C15.4477 15.2575 15.7871 14.3156 15.7271 13.3558L15.3007 6.53347C15.2625 5.92147 14.755 5.4447 14.1418 5.4447Z"
        fill="currentColor"
      />
    </svg>
  </>
));

ShoppingBag.displayName = "ShoppingBag";
export const Star = React.forwardRef(({ className, size }) => (
  <>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
      className={cn(IconVariants({ size, className }))}
    >
      <path
        d="M8.00212 3.1102L6.37046 6.3644L2.75368 6.88152C2.33684 6.93994 1.9504 7.13828 1.65873 7.44413L1.56169 7.55406C0.948387 8.30548 1.00791 9.4144 1.72162 10.095L4.31796 12.5827L3.70143 16.1195C3.62933 16.5255 3.69401 16.9458 3.88569 17.3131L3.95183 17.4296C4.46295 18.2604 5.53742 18.566 6.4155 18.1076L9.68129 16.4202L12.951 18.1096C13.316 18.3001 13.7363 18.3648 14.1442 18.2924L14.2752 18.2643C15.2213 18.0266 15.8357 17.0937 15.6625 16.1185L15.0455 12.5827L17.6437 10.094C17.9485 9.80336 18.1468 9.41692 18.2055 8.99838L18.2194 8.86512C18.2865 7.89197 17.5898 7.01877 16.6089 6.88128L12.993 6.3644L11.3609 3.10765C11.18 2.74264 10.8823 2.44496 10.5147 2.2628C9.58689 1.80296 8.46196 2.18236 8.00212 3.1102Z"
        fill="#2255F9"
      />
    </svg>
  </>
));

Star.displayName = "Star";

export const ThumbUp = React.forwardRef(({ className, size, ...props }, ref) => (
  <>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className={cn(IconVariants({ size, className }))}
    >
      <path
        d="M14.0003 5.04548C14.2443 4.80134 14.64 4.80123 14.8841 5.04525C15.1061 5.26708 15.1264 5.6143 14.9449 5.85902L14.8844 5.92913L12.9756 7.83886C12.7538 8.06077 12.4067 8.08106 12.1619 7.89964L12.0918 7.83917L10.945 6.69333C10.7008 6.44936 10.7006 6.05364 10.9446 5.80945C11.1664 5.58746 11.5136 5.56714 11.7583 5.74857L11.8285 5.80906L12.5332 6.51248L14.0003 5.04548Z"
        fill="currentColor"
      />
      <path
        d="M12.7553 1.04398C15.5684 0.957976 17.9408 3.12271 18.1121 5.93191C18.2834 8.74111 16.1917 11.1781 13.389 11.4346C13.0453 11.4661 12.7411 11.2129 12.7096 10.8692C12.6782 10.5254 12.9313 10.2213 13.2751 10.1898C15.405 9.99488 16.9946 8.14287 16.8644 6.008C16.7342 3.87314 14.9313 2.22804 12.7935 2.2934C10.6556 2.35876 8.95658 4.11092 8.95703 6.24989C8.95703 6.35851 8.96319 6.47398 8.9755 6.61031C9.00656 6.95409 8.75306 7.25796 8.40928 7.28902C8.0655 7.32008 7.76163 7.06657 7.73058 6.72279C7.71512 6.55171 7.70703 6.40002 7.70703 6.25002C7.70644 3.4356 9.94216 1.12998 12.7553 1.04398Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2.91667 11.7188H4.58333C5.15863 11.7188 5.625 12.1851 5.625 12.7604V17.0833C5.625 17.6586 5.15863 18.125 4.58333 18.125H2.91667C2.34137 18.125 1.875 17.6586 1.875 17.0833V12.7604C1.875 12.1851 2.34137 11.7188 2.91667 11.7188ZM4.375 16.875V12.9683H3.125V16.875H4.375Z"
        fill="currentColor"
      />
      <path
        d="M10.6763 12.1528C11.3136 12.1528 11.9141 12.452 12.2979 12.9609C12.6497 13.4273 12.7822 14.0204 12.6669 14.588L12.6293 14.7421L12.0513 16.7651C11.8349 17.5227 11.1704 18.0589 10.3951 18.1193L10.2485 18.125H6.84063C6.47421 18.125 6.1178 18.0133 5.81814 17.8067L5.69332 17.7125L4.68014 16.8756C4.414 16.6558 4.37644 16.2619 4.59625 15.9957C4.79608 15.7538 5.13982 15.7008 5.40062 15.8583L5.47615 15.9119L6.48935 16.7487C6.56844 16.814 6.66376 16.8557 6.76436 16.8697L6.84063 16.875H10.2485C10.4965 16.875 10.7178 16.7289 10.8178 16.5079L10.8494 16.4217L11.4274 14.3987C11.4948 14.163 11.4476 13.9093 11.3 13.7136C11.1734 13.5459 10.9857 13.4374 10.7802 13.4098L10.6763 13.4028H9.16669C8.82151 13.4028 8.54169 13.123 8.54169 12.7778C8.54169 12.4614 8.77682 12.1999 9.08188 12.1585L9.16669 12.1528H10.6763Z"
        fill="currentColor"
      />
      <path
        d="M6.06163 10.3378C6.53326 9.56962 7.45791 9.20841 8.32536 9.45346C9.14461 9.6849 9.72438 10.4039 9.78627 11.2424L9.79175 11.3915V12.778C9.79175 13.1232 9.51193 13.403 9.16675 13.403C8.85034 13.403 8.58885 13.1679 8.54746 12.8628L8.54175 12.778V11.3915C8.54175 11.0496 8.31456 10.7493 7.98553 10.6564C7.6894 10.5727 7.3757 10.6753 7.1855 10.9091L7.12687 10.9918L5.5327 13.5882C5.35209 13.8824 4.96722 13.9744 4.67306 13.7938C4.40342 13.6282 4.30361 13.291 4.42796 13.0094L4.46747 12.9342L6.06163 10.3378Z"
        fill="currentColor"
      />
    </svg>
  </>
));

ThumbUp.displayName = "ThumbUp";

export const ShoppingBagWithTag = React.forwardRef(({ className, size, ...props }, ref) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="18"
    viewBox="0 0 16 18"
    fill="none"
    className={cn(IconVariants({ size, className }))}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.45701 10.25C9.45701 9.21447 8.61754 8.375 7.58201 8.375H5.22543C4.72819 8.375 4.25131 8.57251 3.89968 8.92409L1.25626 11.5672C0.904592 11.9188 0.707031 12.3958 0.707031 12.8931C0.707031 13.3904 0.904592 13.8673 1.25625 14.2189L3.61321 16.5759C4.34544 17.3081 5.53261 17.3081 6.26484 16.5759L8.90784 13.9328C9.25947 13.5811 9.45701 13.1042 9.45701 12.6069V10.25ZM5.22543 9.625H7.58201C7.92719 9.625 8.20701 9.90482 8.20701 10.25V12.6069C8.20701 12.7727 8.14116 12.9317 8.02395 13.0489L5.38095 15.692C5.13689 15.936 4.74116 15.936 4.49708 15.692L2.14011 13.335C2.02288 13.2178 1.95703 13.0588 1.95703 12.8931C1.95703 12.7273 2.02288 12.5683 2.1401 12.4511L4.78351 9.80803C4.90071 9.69084 5.05968 9.625 5.22543 9.625Z"
      fill="currentColor"
    />
    <path
      d="M13.832 4.20801C14.6374 4.20801 15.2904 4.86093 15.2904 5.66634V14.833C15.2904 16.0987 14.2643 17.1247 12.9987 17.1247H8.83203C8.48685 17.1247 8.20703 16.8449 8.20703 16.4997C8.20703 16.1545 8.48685 15.8747 8.83203 15.8747H12.9987C13.574 15.8747 14.0404 15.4083 14.0404 14.833V5.66634C14.0404 5.55128 13.9471 5.45801 13.832 5.45801H2.16536C2.05031 5.45801 1.95703 5.55128 1.95703 5.66634V8.16634C1.95703 8.51152 1.67721 8.79134 1.33203 8.79134C0.986853 8.79134 0.707031 8.51152 0.707031 8.16634V5.66634C0.707031 4.86093 1.35995 4.20801 2.16536 4.20801H13.832Z"
      fill="currentColor"
    />
    <path
      d="M7.9987 0.0415039C9.95471 0.0415039 11.5404 1.62716 11.5404 3.58317V4.83317C11.5404 5.17835 11.2605 5.45817 10.9154 5.45817C10.5702 5.45817 10.2904 5.17835 10.2904 4.83317V3.58317C10.2904 2.31752 9.26435 1.2915 7.9987 1.2915C6.73305 1.2915 5.70703 2.31752 5.70703 3.58317V4.83317C5.70703 5.17835 5.42721 5.45817 5.08203 5.45817C4.73685 5.45817 4.45703 5.17835 4.45703 4.83317V3.58317C4.45703 1.62716 6.04269 0.0415039 7.9987 0.0415039Z"
      fill="currentColor"
    />
    <path
      d="M5.89009 11.9419L5.82957 11.8718C5.66618 11.6516 5.66618 11.3483 5.82957 11.1281L5.89009 11.058L5.9606 10.9971C6.20528 10.8155 6.5525 10.8357 6.77439 11.0576L6.83532 11.1281C7.01687 11.3728 6.9967 11.72 6.77481 11.9419C6.64167 12.075 6.46342 12.1355 6.28927 12.1234C6.14331 12.1133 6.00103 12.0528 5.89009 11.9419Z"
      fill="currentColor"
    />
    <path
      d="M6.37526 10.877C6.52231 10.8866 6.66459 10.9471 6.77554 11.058C7.01961 11.3021 7.01961 11.6978 6.77554 11.9419C6.53062 12.1868 6.1349 12.1868 5.89082 11.9427C5.64591 11.6978 5.64591 11.3021 5.88999 11.058C6.00093 10.9471 6.14321 10.8866 6.28833 10.8765L6.37526 10.877Z"
      fill="currentColor"
    />
  </svg>
));

ShoppingBagWithTag.displayName = "ShoppingBagWithTag";

export const Exchange = React.forwardRef(({ className, size, ...props }, ref) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    className={cn(IconVariants({ size, className }))}
  >
    <path
      d="M6.22603 2.89155C6.4701 2.64748 6.86583 2.64748 7.10991 2.89155C7.3318 3.11344 7.35197 3.46066 7.17043 3.70534L7.10991 3.77544L5.85991 5.02544C5.61583 5.26952 5.2201 5.26952 4.97603 5.02544C4.75414 4.80355 4.73397 4.45633 4.91551 4.21165L4.97603 4.14155L6.22603 2.89155Z"
      fill="currentColor"
    />
    <path
      d="M4.97603 1.64155C5.19792 1.41967 5.54514 1.39949 5.78981 1.58104L5.85991 1.64155L7.10991 2.89155C7.35399 3.13563 7.35399 3.53136 7.10991 3.77544C6.88802 3.99733 6.5408 4.0175 6.29613 3.83595L6.22603 3.77544L4.97603 2.52544C4.73195 2.28136 4.73195 1.88563 4.97603 1.64155Z"
      fill="currentColor"
    />
    <path
      d="M6.66667 2.7085C7.01184 2.7085 7.29167 2.98832 7.29167 3.3335C7.29167 3.64991 7.05654 3.91141 6.75148 3.95279L6.66667 3.9585H5C3.96434 3.9585 3.125 4.79784 3.125 5.8335C3.125 6.17867 2.84518 6.4585 2.5 6.4585C2.15482 6.4585 1.875 6.17867 1.875 5.8335C1.875 4.16316 3.18519 2.79909 4.83402 2.71283L5 2.7085H6.66667Z"
      fill="currentColor"
    />
    <path
      d="M14.1401 14.9751C14.3842 14.731 14.7799 14.731 15.024 14.9751C15.2459 15.1969 15.266 15.5442 15.0845 15.7888L15.024 15.8589L13.774 17.1089C13.5299 17.353 13.1342 17.353 12.8901 17.1089C12.6682 16.887 12.648 16.5398 12.8296 16.2951L12.8901 16.2251L14.1401 14.9751Z"
      fill="currentColor"
    />
    <path
      d="M12.8901 16.2251C13.112 16.0032 13.4592 15.983 13.7039 16.1645L13.774 16.2251L15.024 17.4751C15.2681 17.7191 15.2681 18.1149 15.024 18.3589C14.8021 18.5808 14.4549 18.601 14.2102 18.4194L14.1401 18.3589L12.8901 17.1089C12.646 16.8649 12.646 16.4691 12.8901 16.2251Z"
      fill="currentColor"
    />
    <path
      d="M17.4987 13.542C17.8439 13.542 18.1237 13.8218 18.1237 14.167C18.1237 15.8373 16.8135 17.2014 15.1647 17.2877L14.9987 17.292H13.332C12.9869 17.292 12.707 17.0122 12.707 16.667C12.707 16.3506 12.9422 16.0891 13.2472 16.0477L13.332 16.042H14.9987C15.9912 16.042 16.8034 15.2711 16.8694 14.2954L16.8737 14.167C16.8737 13.8218 17.1535 13.542 17.4987 13.542Z"
      fill="currentColor"
    />
    <path
      d="M8.4007 4.23429C10.4273 2.20771 13.7397 2.20771 15.7662 4.23429C17.7928 6.26087 17.7928 9.57326 15.7662 11.5998C15.6158 11.7502 15.4678 11.8704 15.2388 12.0389C14.9607 12.2434 14.5695 12.1838 14.365 11.9057C14.1605 11.6276 14.2201 11.2364 14.4982 11.0319C14.6804 10.8979 14.787 10.8114 14.8824 10.716C16.4208 9.17753 16.4208 6.65659 14.8824 5.11817C13.3439 3.57975 10.823 3.57975 9.28458 5.11817C9.22568 5.17707 9.16936 5.24007 9.1042 5.31992L8.95873 5.50587C8.74998 5.78077 8.3579 5.8344 8.083 5.62565C7.80809 5.4169 7.75447 5.02483 7.96322 4.74992C8.13141 4.52843 8.25345 4.38153 8.4007 4.23429Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.1276 12.0842C13.1276 9.21872 10.7842 6.875 7.91844 6.875C5.0524 6.875 2.71094 9.21735 2.71094 12.0842C2.71094 13.2199 3.08447 14.3043 3.76063 15.2032C4.74172 16.5061 6.26892 17.2917 7.91844 17.2917C10.7844 17.2917 13.1276 14.9494 13.1276 12.0842ZM3.96094 12.0842C3.96094 9.9076 5.74286 8.125 7.91844 8.125C10.0938 8.125 11.8776 9.90904 11.8776 12.0842L11.8735 12.2645C11.7809 14.2968 10.1321 15.945 8.09881 16.0376L7.91844 16.0417L7.71083 16.0362C6.53979 15.9741 5.46592 15.3898 4.75939 14.4515C4.24398 13.7663 3.96094 12.9446 3.96094 12.0842Z"
      fill="currentColor"
    />
    <path
      d="M7.91797 9.16748C8.23438 9.16748 8.49588 9.40261 8.53726 9.70767L8.54297 9.79248V10.2091C8.54297 10.5543 8.26315 10.8341 7.91797 10.8341C7.60156 10.8341 7.34006 10.599 7.29867 10.294L7.29297 10.2091V9.79248C7.29297 9.4473 7.57279 9.16748 7.91797 9.16748Z"
      fill="currentColor"
    />
    <path
      d="M7.91797 13.3345C8.23438 13.3345 8.49588 13.5696 8.53726 13.8747L8.54297 13.9595V14.3761C8.54297 14.7213 8.26315 15.0011 7.91797 15.0011C7.60156 15.0011 7.34006 14.766 7.29867 14.4609L7.29297 14.3761V13.9595C7.29297 13.6143 7.57279 13.3345 7.91797 13.3345Z"
      fill="currentColor"
    />
    <path
      d="M8.23047 9.5835C8.77509 9.5835 9.25461 9.87174 9.5344 10.3289C9.71458 10.6233 9.62197 11.0081 9.32755 11.1883C9.05766 11.3534 8.71189 11.2894 8.51734 11.0508L8.46821 10.9814C8.42044 10.9034 8.35729 10.8555 8.28566 10.8395L8.23047 10.8335H7.54547C7.4061 10.8335 7.29297 10.9466 7.29297 11.086C7.29297 11.1823 7.34771 11.2685 7.43108 11.3108L7.48433 11.3306L8.65661 11.625C9.32431 11.7933 9.79297 12.3939 9.79297 13.0827C9.79297 13.8707 9.18602 14.5175 8.41372 14.5802L8.29047 14.5852H7.60547C7.05974 14.5852 6.57911 14.2958 6.29942 13.8373C6.11965 13.5427 6.2128 13.158 6.50747 12.9783C6.77759 12.8135 7.12327 12.878 7.31748 13.1169L7.36652 13.1863C7.41436 13.2648 7.47794 13.313 7.54997 13.3291L7.60547 13.3352H8.29047C8.42991 13.3352 8.54297 13.222 8.54297 13.0827C8.54297 12.9862 8.48799 12.8997 8.40474 12.8572L8.35161 12.8372L7.18055 12.5432C6.5122 12.3761 6.04297 11.7752 6.04297 11.086C6.04297 10.2975 6.65031 9.65114 7.42228 9.58848L7.54547 9.5835H8.23047Z"
      fill="currentColor"
    />
  </svg>
));

Exchange.displayName = "Exchange";

export const Rocket = React.forwardRef(({ className, size }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="40"
    height="40"
    viewBox="0 0 40 40"
    fill="none"
    className={cn(IconVariants({ size, className }))}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M32.771 3.75867L33.3344 3.75C34.8189 3.75 36.0615 4.864 36.2378 6.40031L36.2511 6.66668C36.2511 12.3136 33.6842 17.6543 29.2747 21.1819L20.2801 28.3775C19.1196 29.3059 17.4466 29.2133 16.3957 28.1624L11.8387 23.6054C10.7878 22.5545 10.6951 20.8815 11.6235 19.7209L18.8192 10.7264C22.2208 6.47429 27.3144 3.92943 32.771 3.75867ZM33.3344 6.25L32.8297 6.2579C28.1241 6.4053 23.7155 8.60791 20.7714 12.2881L13.5757 21.2827C13.4431 21.4485 13.4563 21.6875 13.6064 21.8376L18.1635 26.3946C18.3136 26.5448 18.5526 26.558 18.7184 26.4254L27.7129 19.2297C31.5294 16.1766 33.7511 11.5541 33.7531 6.73867L33.7482 6.61651C33.7242 6.40957 33.5462 6.25 33.3344 6.25Z"
      fill="currentColor"
    />
    <path
      d="M26.6657 20.4165C27.2985 20.4165 27.8215 20.8868 27.9043 21.4969L27.9157 21.6665V28.9698C27.9157 29.9896 27.3839 30.9286 26.5249 31.4556L26.3034 31.5786L22.2769 33.5919C20.8361 34.3123 19.0841 33.7283 18.3638 32.2875L18.2779 32.0993L18.2055 31.9055L17.1465 28.7285C16.9282 28.0735 17.2822 27.3656 17.9371 27.1473C18.5374 26.9472 19.1823 27.2279 19.4538 27.7806L19.5182 27.9379L20.5772 31.1149L20.5998 31.1695C20.6856 31.341 20.8737 31.4275 21.0531 31.3921L21.1588 31.3558L25.1854 29.3425C25.2983 29.286 25.378 29.1824 25.4053 29.0624L25.4157 28.9698V21.6665C25.4157 20.9761 25.9753 20.4165 26.6657 20.4165Z"
      fill="currentColor"
    />
    <path
      d="M18.3356 12.0835C19.0259 12.0835 19.5856 12.6431 19.5856 13.3335C19.5856 13.9663 19.1153 14.4893 18.5052 14.5721L18.3356 14.5835H11.0322C10.9061 14.5835 10.7885 14.6406 10.7104 14.7357L10.6597 14.8138L8.64634 18.8404L8.62374 18.8949C8.56309 19.0769 8.63492 19.2711 8.78688 19.3728L8.88726 19.422L12.0642 20.481C12.7191 20.6993 13.0731 21.4072 12.8548 22.0621C12.6546 22.6625 12.0431 23.0099 11.4381 22.8955L11.2736 22.8527L8.09668 21.7937C6.56852 21.2843 5.74264 19.6325 6.25203 18.1044L6.32437 17.9105L6.41028 17.7223L8.4236 13.6958C8.87964 12.7837 9.77521 12.1817 10.7792 12.0945L11.0322 12.0835H18.3356Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.05581 26.3644L8.3807 26.303C10.9335 25.9194 13.314 27.6779 13.6976 30.2308C13.7436 30.5374 13.7591 30.8479 13.7391 31.2194L13.6976 31.6199C13.3926 33.6495 11.7992 35.2428 9.76958 35.5477L5.18608 36.2362C4.35454 36.3612 3.63932 35.646 3.76424 34.8144L4.45283 30.2309C4.73742 28.3365 6.14838 26.8108 8.05581 26.3644ZM11.2253 30.6022C11.0469 29.4148 9.93962 28.5968 8.80218 28.7667L8.57707 28.8089C7.71394 29.0114 7.05744 29.7213 6.92508 30.6023L6.48704 33.5118L9.3982 33.0754C10.2697 32.9445 10.9682 32.303 11.1761 31.511L11.2176 31.3101L11.2468 31.0333C11.2539 30.8893 11.2468 30.7449 11.2253 30.6022Z"
      fill="currentColor"
    />
  </svg>
));

Rocket.displayName = "Rocket";

export const Secure = React.forwardRef(({ className, size }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="40"
    height="40"
    viewBox="0 0 40 40"
    fill="none"
    className={cn(IconVariants({ size, className }))}
  >
    <path
      d="M34.9987 22.0835C35.6315 22.0835 36.1545 22.5538 36.2373 23.1639L36.2487 23.3335V30.0002C36.2487 33.3406 33.6281 36.069 30.3306 36.2415L29.9987 36.2502H28.332C27.6417 36.2502 27.082 35.6905 27.082 35.0002C27.082 34.3673 27.5523 33.8443 28.1624 33.7616L28.332 33.7502H29.9987C31.9835 33.7502 33.6081 32.2082 33.74 30.2569L33.7487 30.0002V23.3335C33.7487 22.6431 34.3083 22.0835 34.9987 22.0835Z"
      fill="currentColor"
    />
    <path
      d="M15 3.75C15.6904 3.75 16.25 4.30964 16.25 5C16.25 5.63283 15.7797 6.15582 15.1696 6.23859L15 6.25H10C8.01523 6.25 6.39059 7.79194 6.25865 9.74325L6.25 10V15C6.25 15.6904 5.69036 16.25 5 16.25C4.36717 16.25 3.84418 15.7797 3.76141 15.1696L3.75 15V10C3.75 6.65957 6.3706 3.93121 9.66807 3.75866L10 3.75H15Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M24.1667 3.75H30.8333C33.8249 3.75 36.25 6.17512 36.25 9.16667V11.8406C36.25 16.2024 33.2744 19.9971 29.0245 21.0499L28.6278 21.1383C28.0329 21.2573 27.4232 21.2813 26.7723 21.203L26.3723 21.1383C21.9403 20.2519 18.75 16.3605 18.75 11.8407V9.16667C18.75 6.17512 21.1751 3.75 24.1667 3.75ZM30.8333 6.25H24.1667C22.5558 6.25 21.25 7.55584 21.25 9.16667V11.8407C21.25 15.1688 23.5991 18.0342 26.8139 18.6782L27.1163 18.7273C27.4565 18.7677 27.8011 18.7541 28.1097 18.6927L28.451 18.6167C31.5587 17.8466 33.75 15.0522 33.75 11.8406V9.16667C33.75 7.55584 32.4442 6.25 30.8333 6.25Z"
      fill="currentColor"
    />
    <path
      d="M20.8336 28.75C22.9047 28.75 24.5836 30.4289 24.5836 32.5C24.5836 34.4848 23.0417 36.1094 21.0904 36.2413L20.8336 36.25H9.90657C8.83172 36.25 7.76979 36.0311 6.78505 35.6083L6.36792 35.4147L4.44126 34.4514C3.82378 34.1426 3.5735 33.3918 3.88224 32.7743C4.16525 32.2083 4.81975 31.9508 5.40248 32.1497L5.55929 32.2153L7.48593 33.1786C8.13013 33.5007 8.83104 33.6904 9.54734 33.7381L9.90657 33.75H20.8336C21.524 33.75 22.0836 33.1904 22.0836 32.5C22.0836 31.8672 21.6133 31.3442 21.0032 31.2614L20.8336 31.25H14.1669C13.4766 31.25 12.9169 30.6904 12.9169 30C12.9169 29.3672 13.3872 28.8442 13.9973 28.7614L14.1669 28.75H20.8336Z"
      fill="currentColor"
    />
    <path
      d="M9.85558 20.4165C10.7371 20.4165 11.5975 20.6706 12.335 21.145L12.6056 21.3332L16.3344 24.1298C17.2517 24.8179 17.8254 25.8701 17.9067 27.0139C17.9879 28.1577 17.5689 29.2805 16.758 30.0913C15.4394 31.41 13.3922 31.62 11.8413 30.6341L11.5876 30.4588L9.24998 28.7054C8.6977 28.2912 8.58579 27.5077 9.00002 26.9554C9.37659 26.4533 10.0583 26.3152 10.5941 26.6055L10.75 26.7055L13.0877 28.4588C13.6672 28.8934 14.4781 28.8358 14.9903 28.3236C15.2886 28.0252 15.4429 27.612 15.4129 27.1911C15.3873 26.8304 15.2286 26.4943 14.9711 26.2462L14.8344 26.1298L11.1056 23.3332C10.8051 23.1078 10.4504 22.9687 10.0797 22.9286L9.85558 22.9165H5C4.30964 22.9165 3.75 22.3569 3.75 21.6665C3.75 21.0337 4.22026 20.5107 4.83038 20.4279L5 20.4165H9.85558Z"
      fill="currentColor"
    />
  </svg>
));

Secure.displayName = "Secure";

export const Flash = React.forwardRef(({ className, size }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    className={cn(IconVariants({ size, className }))}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M3.72482 10.0881L3.78756 9.98996L8.79693 2.75384C9.61397 1.57498 11.463 2.15246 11.463 3.58728L11.4629 7.71478L15.0096 7.71549C16.15 7.71549 16.837 8.95428 16.275 9.9139L16.2124 10.0121L11.203 17.2482C10.386 18.4271 8.53693 17.8496 8.53693 16.4148L8.53623 12.2865L4.99039 12.2866C3.84907 12.2866 3.16175 11.0474 3.72482 10.0881ZM10.1811 8.35645V3.58728C10.1811 3.40915 9.95203 3.33761 9.85073 3.48378L4.84135 10.7199C4.75784 10.8404 4.84391 11.0047 4.99039 11.0047H9.17789C9.53188 11.0047 9.81885 11.2916 9.81885 11.6456V16.4148C9.81885 16.5929 10.0479 16.6645 10.1492 16.5183L15.1582 9.2827C15.2416 9.16212 15.1553 8.99741 15.0096 8.99741H10.8221C10.4681 8.99741 10.1811 8.71044 10.1811 8.35645Z"
      fill="currentColor"
    />
  </svg>
));

Flash.displayName = "Flash";
